import { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/ui/Layout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import { useTranslation } from '../../i18n/LanguageContext';
import { IconCreditCard, IconCheck, IconLock } from '@tabler/icons-react';

const STATUS_LABELS = {
  active: { labelKey: 'status.active', color: 'var(--color-success)' },
  trialing: { labelKey: 'set.status_trialing', color: 'var(--color-success)' },
  past_due: { labelKey: 'set.status_past_due', color: 'var(--color-warning)' },
  canceled: { labelKey: 'set.status_cancelled', color: 'var(--color-danger)' },
  unpaid: { labelKey: 'set.status_unpaid', color: 'var(--color-danger)' },
  incomplete: { labelKey: 'set.status_incomplete', color: 'var(--color-warning)' },
};

const STANDARD_FEATURE_KEYS = [
  'set.feat_registry',
  'set.feat_checks',
  'set.feat_defects',
  'upsell.derv_title',
  'set.feat_staff',
  'set.feat_reporting',
  'set.feat_unlimited',
];

function formatDate(timestamp, locale) {
  if (!timestamp) return '—';
  const d = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return d.toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function Billing() {
  const { t, language } = useTranslation();
  const { org, role } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const planStatus = org?.plan?.status || null;
  const periodEnd = org?.plan?.currentPeriodEnd || null;
  const statusInfo = STATUS_LABELS[planStatus]
    ? { label: t(STATUS_LABELS[planStatus].labelKey), color: STATUS_LABELS[planStatus].color }
    : { label: planStatus || t('common.unknown'), color: 'var(--color-text-tertiary)' };
  const isGrandfathered = org?.plan?.source === 'grandfathered';

  const handleManageBilling = async () => {
    setError('');
    setLoading(true);
    try {
      const createPortal = httpsCallable(functions, 'createCustomerPortalSession');
      const result = await createPortal({ baseUrl: window.location.origin });
      const url = result.data?.url;
      if (!url) throw new Error(t('set.err_no_portal'));
      window.location.href = url;
    } catch (err) {
      console.error('Portal session failed', err);
      setError(err.message || t('set.err_portal'));
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)', maxWidth: 800, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          <IconCreditCard size={20} color="var(--color-text-secondary)" stroke={1.5} />
          <h1 style={{ fontSize: 22, fontWeight: 400 }}>{t('set.billing_title')}</h1>
        </div>
        <p style={{ fontSize: 13, color: 'var(--color-text-tertiary)', marginBottom: 24 }}>
          {t('set.billing_subtitle')}
        </p>

        <Card style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
            <div>
              <div style={{ fontSize: 16, fontWeight: 500, marginBottom: 4 }}>{t('set.plan_standard')}</div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                <span style={{ fontSize: 28, fontWeight: 400 }}>£99</span>
                <span style={{ fontSize: 13, color: 'var(--color-text-tertiary)' }}>{t('set.per_month')}</span>
              </div>
            </div>
            <div style={{ padding: '4px 10px', borderRadius: 'var(--radius-full)', background: statusInfo.color + '18', color: statusInfo.color, fontSize: 11, fontWeight: 500 }}>
              {statusInfo.label}
            </div>
          </div>

          {isGrandfathered && (
            <div style={{ padding: '10px 14px', background: 'var(--color-bg-secondary)', borderRadius: 'var(--radius-md)', fontSize: 12, color: 'var(--color-text-secondary)', marginBottom: 16, lineHeight: 1.6 }}>
              {t('set.grandfathered')}
            </div>
          )}

          {periodEnd && !isGrandfathered && (
            <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginBottom: 16 }}>
              {t('set.next_billing')} {formatDate(periodEnd, language)}
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 7, marginBottom: 20 }}>
            {STANDARD_FEATURE_KEYS.map(f => (
              <div key={f} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <IconCheck size={13} color="var(--color-success)" stroke={2} />
                <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>{t(f)}</span>
              </div>
            ))}
          </div>

          {error && (
            <div style={{ marginBottom: 14, padding: '10px 12px', background: 'var(--color-danger-light)', color: 'var(--color-danger)', borderRadius: 'var(--radius-sm)', fontSize: 12 }}>
              {error}
            </div>
          )}

          {role === 'org_admin' ? (
            <Button variant="primary" onClick={handleManageBilling} disabled={loading || isGrandfathered}>
              {loading ? t('set.opening_portal') : t('set.manage_billing')}
            </Button>
          ) : (
            <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}>
              {t('set.admin_only_billing')}
            </div>
          )}
        </Card>

        <Card>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <IconLock size={14} color="var(--color-text-tertiary)" stroke={1.5} />
            <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
              {t('set.stripe_note')}
            </div>
          </div>
        </Card>
      </div>
    </Layout>
  );
}
